import React, { useRef } from 'react';
import { Project } from '../../../types';

interface ProjectTitleProps {
  project: Project;
  canEdit: boolean;
  onBlur: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onStatusChange?: (status: string) => void;
}

const STATUS_SEQUENCE = ['planning', 'development', 'testing', 'sit', 'uat', 'done', 'production', 'archived'] as const;
const STATUS_LABELS: Record<string, string> = {
  planning: 'Planning', development: 'Development', testing: 'Testing',
  sit: 'SIT', uat: 'UAT', done: 'Done', production: 'Production', archived: 'Archived',
};
const STATUS_COLORS: Record<string, { active: string; done: string }> = {
  planning: { active: 'bg-gray-500 text-white', done: 'bg-gray-400 text-white' },
  development: { active: 'bg-blue-600 text-white', done: 'bg-blue-400 text-white' },
  testing: { active: 'bg-yellow-500 text-white', done: 'bg-yellow-400 text-white' },
  sit: { active: 'bg-orange-500 text-white', done: 'bg-orange-400 text-white' },
  uat: { active: 'bg-purple-600 text-white', done: 'bg-purple-400 text-white' },
  done: { active: 'bg-green-600 text-white', done: 'bg-green-400 text-white' },
  production: { active: 'bg-emerald-600 text-white', done: 'bg-emerald-400 text-white' },
  archived: { active: 'bg-gray-600 text-white', done: 'bg-gray-400 text-white' },
};

const ProjectTitle: React.FC<ProjectTitleProps> = ({
  project,
  canEdit,
  onBlur,
  onStatusChange,
}) => {
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const currentIdx = STATUS_SEQUENCE.indexOf(project.status as any);

  return (
    <div className="mb-6">
      <textarea
        ref={titleRef}
        defaultValue={project.name}
        onBlur={onBlur}
        placeholder="Project name"
        className="w-full text-3xl font-semibold text-gray-900 resize-none border-none focus:outline-none focus:ring-0 p-0"
        rows={1}
        disabled={!canEdit}
        onInput={(e) => {
          e.currentTarget.style.height = 'auto';
          e.currentTarget.style.height = e.currentTarget.scrollHeight + 'px';
        }}
      />

      {/* Status progression stepper */}
      <div className="flex items-center gap-1 mt-3 flex-wrap">
        {STATUS_SEQUENCE.map((status, idx) => {
          const isCurrent = idx === currentIdx;
          const isPast = idx < currentIdx;
          const colors = STATUS_COLORS[status];
          const cls = isCurrent
            ? `${colors.active} ring-2 ring-offset-1 ring-current`
            : isPast
            ? colors.done
            : 'bg-gray-100 text-gray-400';

          return (
            <React.Fragment key={status}>
              {idx > 0 && (
                <div className={`w-3 h-px ${idx <= currentIdx ? 'bg-gray-400' : 'bg-gray-200'}`} />
              )}
              {canEdit && onStatusChange ? (
                <button
                  onClick={() => onStatusChange(status)}
                  title={`Set status to ${STATUS_LABELS[status]}`}
                  className={`px-2.5 py-1 text-[11px] font-medium rounded-full transition-all hover:ring-2 hover:ring-offset-1 hover:ring-blue-400 ${cls}`}
                >
                  {STATUS_LABELS[status]}
                </button>
              ) : (
                <span className={`px-2.5 py-1 text-[11px] font-medium rounded-full ${cls}`}>
                  {STATUS_LABELS[status]}
                </span>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default ProjectTitle;
